/**
 * Ranking for the list-a-machine "Search Model #" box.
 *
 * The box used to keep only taxonomy rows whose modelName started with the query.
 * That missed catalog-only models (Caterpillar 255) and ignored the make, so
 * "Cat 255" matched nothing. Callers pass taxonomy rows plus Published catalog
 * rows; this ranks the union and caps only after ranking.
 */

export type ModelSearchSource = "taxonomy" | "catalog";

export interface ModelSearchCandidate {
  manufacturerName: string;
  modelName: string;
  modelId?: number;
  catalogItemId?: string;
  /** Catalog index `createdDateTime`. Used to pick the oldest Published duplicate. */
  createdDateTime?: string;
  source: ModelSearchSource;
}

export interface ModelSearchHit extends ModelSearchCandidate {
  manufacturerName: string;
  modelName: string;
}

/** How many Published catalog docs to pull before client ranking. High so an exact model is not dropped by Azure's relevance order. */
export const PUBLISHED_CATALOG_FETCH_TOP = 1000;

/** Shown in the typeahead. Applied only after exact/prefix ranking. */
export const MODEL_SEARCH_RESULT_CAP = 40;

export const PUBLISHED_MACHINE_CATALOG_FILTER = "status eq 'Published' and definitionType eq 'MachineType'";

/**
 * Only these makes are peeled off the front of a query.
 * Snapshot makes (J&M, LS, Compact, …) stay part of the model number.
 * Longer tokens first so "caterpillar" wins over "cat".
 */
const ALIAS_MAKES: { token: string; canonical: string }[] = [
  { token: "caterpillar", canonical: "caterpillar" },
  { token: "cat", canonical: "caterpillar" },
];

const MAKE_ALIASES: { [alias: string]: string } = {
  cat: "caterpillar",
};

interface PreparedCandidate {
  make: string;
  modelTyped: string;
  modelCompact: string;
  displayMake: string;
  displayModel: string;
}

const preparedCandidates = new WeakMap<ModelSearchCandidate, PreparedCandidate>();

export interface PublishedCatalogSearch {
  top: number;
  filter: string;
  search: string;
  searchFields: string;
  select: string;
  queryType: string;
  searchMode: string;
}

export function normalizeMake(make: string): string {
  const trimmed = (make || "").replace(/\s+/g, " ").trim().toLowerCase();
  return MAKE_ALIASES[trimmed] || trimmed;
}

export function normalizeModel(model: string): string {
  return (model || "").replace(/\s+/g, " ").trim().toLowerCase();
}

export function buildPublishedCatalogSearch(rawQuery: string): PublishedCatalogSearch | null {
  const needle = modelNeedle(rawQuery);
  if (!needle) {
    return null;
  }
  return {
    top: PUBLISHED_CATALOG_FETCH_TOP,
    filter: PUBLISHED_MACHINE_CATALOG_FILTER,
    search: `${needle} | ${needle}* | "${needle}"`,
    searchFields: "make,model,displayName",
    select: "id,make,model,displayName,createdDateTime",
    queryType: "simple",
    searchMode: "any",
  };
}

export function catalogRowsToCandidates(rows: any[]): ModelSearchCandidate[] {
  return (rows || [])
    .filter(row => row && (row.make || row.model))
    .map(row => ({
      manufacturerName: String(row.make || ""),
      modelName: String(row.model || ""),
      catalogItemId: row.id,
      createdDateTime: row.createdDateTime || row.CreatedDateTime || undefined,
      source: "catalog" as const,
    }));
}

export function rankModelSearch(candidates: ModelSearchCandidate[], rawQuery: string): ModelSearchHit[] {
  const query = (rawQuery || "").replace(/\s+/g, " ").trim().toLowerCase();
  if (query.length < 2) {
    return [];
  }

  const split = splitAliasMake(query);
  const makeQuery = split ? split.make : null;
  const modelQuery = split ? split.model : query;
  if (!modelQuery) {
    return [];
  }

  const best = new Map<string, ScoredHit>();
  for (const candidate of candidates) {
    const scored = scoreCandidate(candidate, makeQuery, modelQuery);
    if (!scored) {
      continue;
    }
    const key = `${scored.make}|${scored.model}`;
    const previous = best.get(key);
    best.set(key, previous ? combine(previous, scored) : scored);
  }

  return Array.from(best.values())
    .sort((a, b) => a.tier - b.tier || a.make.localeCompare(b.make) || a.model.localeCompare(b.model))
    .slice(0, MODEL_SEARCH_RESULT_CAP)
    .map(hit => ({
      manufacturerName: hit.displayMake,
      modelName: hit.displayModel,
      modelId: hit.modelId,
      catalogItemId: hit.catalogItemId,
      source: hit.source,
    }));
}

interface ScoredHit {
  tier: number;
  make: string;
  model: string;
  displayMake: string;
  displayModel: string;
  modelId?: number;
  catalogItemId?: string;
  createdDateTime?: string;
  source: ModelSearchSource;
}

/** Catalog id wins over the snapshot, but the snapshot modelId stays so rate lookup still works. */
function combine(existing: ScoredHit, incoming: ScoredHit): ScoredHit {
  const identity = olderCatalog(existing, incoming);
  return {
    tier: Math.min(existing.tier, incoming.tier),
    make: identity.make,
    model: identity.model,
    displayMake: identity.displayMake,
    displayModel: identity.displayModel,
    modelId: existing.modelId != null ? existing.modelId : incoming.modelId,
    catalogItemId: identity.catalogItemId,
    createdDateTime: identity.createdDateTime,
    source: identity.catalogItemId ? "catalog" : identity.source,
  };
}

function olderCatalog(a: ScoredHit, b: ScoredHit): ScoredHit {
  if (a.catalogItemId && !b.catalogItemId) {
    return a;
  }
  if (b.catalogItemId && !a.catalogItemId) {
    return b;
  }
  if (!a.catalogItemId && !b.catalogItemId) {
    return a;
  }
  return compareOldest(a, b) <= 0 ? a : b;
}

/** Negative when `a` should win: earlier created date, then smaller id when dates match or are missing. */
function compareOldest(a: ScoredHit, b: ScoredHit): number {
  const aTime = createdTime(a.createdDateTime);
  const bTime = createdTime(b.createdDateTime);
  if (aTime != null && bTime != null && aTime !== bTime) {
    return aTime - bTime;
  }
  if (aTime != null && bTime == null) {
    return -1;
  }
  if (aTime == null && bTime != null) {
    return 1;
  }
  return (a.catalogItemId || "").localeCompare(b.catalogItemId || "");
}

function createdTime(value?: string): number | null {
  if (!value) {
    return null;
  }
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
}

function scoreCandidate(candidate: ModelSearchCandidate, makeQuery: string | null, modelQuery: string): ScoredHit | null {
  const prepared = prepareCandidate(candidate);
  if (!prepared.make || !prepared.modelTyped) {
    return null;
  }
  if (makeQuery && !makeMatches(prepared.make, makeQuery)) {
    return null;
  }

  const queryTyped = normalizeModel(modelQuery);
  const queryCompact = compact(queryTyped);
  if (!queryCompact) {
    return null;
  }

  const tier = matchTier(prepared.modelTyped, prepared.modelCompact, queryTyped, queryCompact);
  if (tier == null) {
    return null;
  }

  return {
    tier,
    make: prepared.make,
    model: prepared.modelTyped,
    displayMake: prepared.displayMake,
    displayModel: prepared.displayModel,
    modelId: candidate.modelId,
    catalogItemId: candidate.catalogItemId,
    createdDateTime: candidate.createdDateTime,
    source: candidate.source,
  };
}

/**
 * Peel cat/caterpillar only, and only when a digit or separator follows the make.
 * "CAT255", "CAT-255", "CAT 255", "Caterpillar255" split. "JM-704" and "LS400" do not.
 */
function splitAliasMake(query: string): { make: string; model: string } | null {
  for (const entry of ALIAS_MAKES) {
    if (!query.startsWith(entry.token) || query.length === entry.token.length) {
      continue;
    }
    const next = query.charAt(entry.token.length);
    if (!/[\d\s\-\/\.]/.test(next)) {
      continue;
    }
    const rest = query.slice(entry.token.length).replace(/^[\s\-\/\.]+/, "").trim();
    if (!rest) {
      continue;
    }
    return { make: entry.canonical, model: rest };
  }
  return null;
}

function prepareCandidate(candidate: ModelSearchCandidate): PreparedCandidate {
  const cached = preparedCandidates.get(candidate);
  if (cached) {
    return cached;
  }
  const modelTyped = normalizeModel(candidate.modelName);
  const prepared = {
    make: normalizeMake(candidate.manufacturerName),
    modelTyped,
    modelCompact: compact(modelTyped),
    displayMake: (candidate.manufacturerName || "").replace(/\s+/g, " ").trim(),
    displayModel: (candidate.modelName || "").replace(/\s+/g, " ").trim(),
  };
  preparedCandidates.set(candidate, prepared);
  return prepared;
}

/**
 * Exact and prefix ignore punctuation and whitespace.
 * Prefix does not glue across a space, so "950H" does not prefix-match "950 HAMMER".
 * Contains uses the typed model (lowercase, trimmed), so "255" does not match "25/50".
 */
function matchTier(modelTyped: string, modelCompact: string, queryTyped: string, queryCompact: string): number | null {
  if (modelTyped === queryTyped || modelCompact === queryCompact) {
    return 0;
  }
  if (modelTyped.startsWith(queryTyped) || compactPrefix(modelTyped, queryCompact)) {
    return 1;
  }
  if (modelTyped.includes(queryTyped)) {
    return 2;
  }
  return null;
}

function compactPrefix(modelTyped: string, queryCompact: string): boolean {
  let queryIndex = 0;
  let crossedSpace = false;
  let sawSpace = false;
  for (let i = 0; i < modelTyped.length && queryIndex < queryCompact.length; i++) {
    const ch = modelTyped.charAt(i);
    const alphanumeric = (ch >= "a" && ch <= "z") || (ch >= "0" && ch <= "9");
    if (alphanumeric) {
      if (sawSpace) {
        crossedSpace = true;
      }
      if (ch !== queryCompact.charAt(queryIndex)) {
        return false;
      }
      queryIndex++;
      sawSpace = false;
    } else if (ch === " ") {
      if (queryIndex > 0) {
        sawSpace = true;
      }
    }
  }
  return queryIndex === queryCompact.length && !crossedSpace;
}

function compact(value: string): string {
  return (value || "").replace(/[^a-z0-9]/g, "");
}

function makeMatches(normalizedMake: string, makeQuery: string): boolean {
  if (normalizedMake === makeQuery) {
    return true;
  }
  if (makeQuery.length >= 3 && (normalizedMake.startsWith(makeQuery + " ") || normalizedMake.startsWith(makeQuery))) {
    return true;
  }
  return false;
}

function modelNeedle(rawQuery: string): string | null {
  const query = (rawQuery || "").replace(/\s+/g, " ").trim().toLowerCase();
  if (query.length < 2) {
    return null;
  }
  const split = splitAliasMake(query);
  const rawNeedle = split ? split.model : query;
  const needle = rawNeedle.replace(/[+\-&|!(){}[\]^"~*?:\\/]/g, " ").replace(/\s+/g, " ").trim();
  return needle.length >= 2 ? needle : null;
}
